import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRightIcon, ChevronLeftIcon } from "@heroicons/react/24/outline";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface City {
    id: number;
    name: string;
    place_count: number;
    description?: string;
    image?: string;
}

/* ── Skeleton ── */
const DestinationCardSkeleton: React.FC = () => (
    <div className="flex flex-col items-center gap-2 px-3 py-2">
        <div className="w-[64px] h-[64px] md:w-[84px] md:h-[84px] rounded-xl bg-gray-200 animate-pulse flex-shrink-0" />
        <div className="h-3 w-14 bg-gray-200 rounded animate-pulse" />
    </div>
);

/* ── Card ── */
/* ── Card ── */
const DestinationCard: React.FC<{ city: City }> = ({ city }) => {
    const [loaded, setLoaded] = useState(false);

    const imageUrl = city.image
        ? city.image.startsWith("http")
            ? city.image
            : `${BASE_URL}${city.image}`
        : null;

    return (
        <Link
            to={`/places/city/${encodeURIComponent(city.name)}`}
            className="flex flex-col items-center gap-2 px-3 py-2 group"
        >
            <div className="w-[64px] h-[64px] md:w-[84px] md:h-[84px] rounded-xl overflow-hidden flex-shrink-0 bg-gray-100 relative">
                {imageUrl ? (
                    <>
                        {/* Blurred preview */}
                        <img
                            src={imageUrl}
                            alt=""
                            aria-hidden
                            className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl brightness-90"
                        />
                        {/* Full-res fades in */}
                        <img
                            src={imageUrl}
                            alt={city.name}
                            onLoad={() => setLoaded(true)}
                            className={`absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-all duration-700 ${
                                loaded ? "opacity-100" : "opacity-0"
                            }`}
                        />
                    </>
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300" />
                )}
            </div>
            <span className="text-xs md:text-sm font-myYekanDemibold text-gray-900 whitespace-nowrap text-center w-full block">
                {city.name}
            </span>
        </Link>
    );
};

/* ── Main component ── */
const PopularDestinations: React.FC = () => {
    const scrollRef = useRef<HTMLDivElement>(null);

    /* ── Fetch ── */
    const { data: cities = [], isLoading, isError } = useQuery<City[]>({
        queryKey: ["cities"],
        queryFn: async () => {
            const res = await fetch(`${BASE_URL}/api/cities/`);
            if (!res.ok) throw new Error("خطا در دریافت شهرها");
            const json = await res.json();
            return Array.isArray(json) ? json : json.data || [];
        },
        staleTime: 10 * 60 * 1000,
    });

    /* ── Scroll ── */
    const scroll = (dir: "prev" | "next") => {
        if (!scrollRef.current) return;
        const amount = scrollRef.current.clientWidth * 0.8;
        scrollRef.current.scrollBy({
            left: dir === "next" ? amount : -amount,
            behavior: "smooth",
        });
    };

    /* ── Shared list ── */
    const renderItems = () => {
        if (isLoading)
            return Array.from({ length: 8 }).map((_, i) => (
                <DestinationCardSkeleton key={i} />
            ));
        if (isError)
            return (
                <p className="text-sm text-red-400 font-myYekanFaNum px-4 py-6">
                    خطا در بارگذاری شهرها
                </p>
            );
return cities
    .filter((city) => !!city.image)
    .map((city) => <DestinationCard key={city.id} city={city} />);    };

    return (
        <div className="py-4 px-0 desktop:px-16">
            {/* Header */}
            <div className="flex justify-between items-center mb-4 desktop:px-20 tablet:px-20 px-[16px]">
                <h2 className="text-lg tablet:text-2xl desktop:text-3xl font-myYekanDemibold">
                    مقاصد پرطرفدار
                </h2>
                {/* <Link
                    to="/destinations"
                    className="flex items-center gap-1 text-sm text-gray-800 font-myYekanFaNumMedium"
                >
                    مشاهده همه
                    <ChevronLeftIcon className="h-4 w-4" />
                </Link> */}
            </div>

            {/* Desktop */}
            <div className="hidden md:block relative px-12">
                <div className="flex items-center">
                    <button
                        onClick={() => scroll("prev")}
                        className="w-10 h-10 flex items-center justify-center flex-shrink-0"
                    >
                        <ChevronRightIcon className="h-6 w-6" />
                    </button>

                    <div
                        ref={scrollRef}
                        className="flex overflow-x-auto pb-2 flex-1 scrollbar-hide"
                        style={{ WebkitOverflowScrolling: "touch" }}
                    >
                        {renderItems()}
                    </div>

                    <button
                        onClick={() => scroll("next")}
                        className="w-10 h-10 flex items-center justify-center flex-shrink-0"
                    >
                        <ChevronLeftIcon className="h-6 w-6" />
                    </button>
                </div>
            </div>

            {/* Mobile */}
            <div className="md:hidden">
                <div
                    className="flex overflow-x-auto px-[16px] pb-2 scrollbar-hide"
                    style={{ WebkitOverflowScrolling: "touch" }}
                >
                    {renderItems()}
                </div>
            </div>

            <style>{`
                .scrollbar-hide::-webkit-scrollbar { display: none; }
                .scrollbar-hide { scrollbar-width: none; }
            `}</style>
        </div>
    );
};

export default PopularDestinations;